import React from 'react';
import { useTranslation, UI_LANGUAGES, toApiLanguage } from './i18n.js';
import { apiUrl } from './config.js';

const PROSVETA_COM_LIST_KEY = 'prosveta_com';
const EXCLUDED_LIST_KEYS = ['languages'];

function getSubscriptionsActionParams() {
  if (typeof window === 'undefined' || !window.location) {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get('action') !== 'subscriptions') {
    return null;
  }
  return {
    email: (params.get('email') || '').trim(),
    cle: (params.get('cle') || '').trim(),
  };
}

function buildSubscriptionsRowState(info) {
  const subscribed = info && info.subscribed;
  const isSubscribed = subscribed !== null && subscribed !== undefined;
  const defaultToHtml = !isSubscribed && !!(info && info.list_html);

  return {
    checked: isSubscribed,
    dataId: isSubscribed ? subscribed : null,
    format: defaultToHtml || (info && info.html) ? 'html' : 'text',
    language: (info && info.language) || '',
  };
}

function SubscriptionsRow({ listKey, info, row, isProsvetaCom, onChange }) {
  const { t } = useTranslation();
  const showFormat = !isProsvetaCom && !!(info && info.list_html);

  return (
    <div className={`subscriptions-row${isProsvetaCom ? ' subscriptions-row--prosveta-com' : ''}`}>
      <label className="subscriptions-row-checkbox">
        <input
          type="checkbox"
          checked={row.checked}
          data-id={row.dataId !== null ? row.dataId : undefined}
          data-list={listKey}
          onChange={(event) => onChange({ checked: event.target.checked })}
        />
        <span className="subscriptions-row-name">
          {isProsvetaCom ? t('subscriptionsNewsletterLabel') : (info && info.label) || listKey}
        </span>
      </label>

      {isProsvetaCom && (
        <label className="subscriptions-row-select-group">
          <span className="subscriptions-row-select-label">{t('subscriptionsLanguageLabel')}</span>
          <select
            className="subscriptions-row-select"
            value={row.language}
            onChange={(event) => onChange({ language: event.target.value })}
          >
            {/* Valeurs = codes du serveur d'API (am pour l'arménien, po pour le portugais). */}
            {UI_LANGUAGES.map((language) => (
              <option key={language.code} value={toApiLanguage(language.code)}>
                {language.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {showFormat && (
        <label className="subscriptions-row-select-group">
          <span className="subscriptions-row-select-label">{t('subscriptionsFormatLabel')}</span>
          <select
            className="subscriptions-row-select"
            value={row.format}
            onChange={(event) => onChange({ format: event.target.value })}
          >
            <option value="html">{t('subscriptionsFormatHtml')}</option>
            <option value="text">{t('subscriptionsFormatText')}</option>
          </select>
        </label>
      )}
    </div>
  );
}

function SubscriptionsModal({ lists, email, onUpdate, onDeleteAll, onClose }) {
  const { t } = useTranslation();
  const visibleEntries = React.useMemo(
    () => Object.entries(lists).filter(([key]) => !EXCLUDED_LIST_KEYS.includes(key)),
    [lists]
  );
  const initialRows = React.useMemo(() => {
    const initial = {};
    visibleEntries.forEach(([key, info]) => {
      initial[key] = buildSubscriptionsRowState(info);
    });
    return initial;
  }, [visibleEntries]);
  const [rows, setRows] = React.useState(initialRows);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const updateRow = (key, changes) => {
    setRows((prev) => ({ ...prev, [key]: { ...prev[key], ...changes } }));
  };

  const prosvetaComEntry = visibleEntries.find(([key]) => key === PROSVETA_COM_LIST_KEY);
  const otherEntries = visibleEntries.filter(([key]) => key !== PROSVETA_COM_LIST_KEY);

  const handleUpdateClick = () => {
    const deleteIds = [];
    const addLists = [];
    const modifyEntries = [];
    Object.entries(rows).forEach(([key, row]) => {
      const isProsvetaCom = key === PROSVETA_COM_LIST_KEY;

      if (row.dataId !== null) {
        if (!row.checked) {
          deleteIds.push(row.dataId);
        } else {
          const modifyEntry = { id: row.dataId };
          let hasChange = false;
          if (!isProsvetaCom && row.format !== initialRows[key].format) {
            modifyEntry.html = row.format === 'html' ? 1 : 0;
            hasChange = true;
          }
          if (isProsvetaCom && row.language !== initialRows[key].language) {
            modifyEntry.language = row.language;
            hasChange = true;
          }
          if (hasChange) {
            modifyEntries.push(modifyEntry);
          }
        }
      } else if (row.checked) {
        const entry = { list: key };
        if (!isProsvetaCom) {
          entry.html = row.format === 'html' ? 1 : 0;
        }
        if (isProsvetaCom) {
          entry.language = row.language;
        }
        addLists.push(entry);
      }
    });
    const payload = { delete: deleteIds, add: { email, lists: addLists }, modify: modifyEntries };
    onUpdate(payload);
  };

  const handleDeleteAllClick = () => {
    const deleteIds = Object.values(rows)
      .map((row) => row.dataId)
      .filter((dataId) => dataId !== null);
    onDeleteAll({ delete: deleteIds, deleteall: email });
  };

  return (
    <div className="legal-modal-overlay" onClick={onClose}>
      <div
        className="legal-modal subscriptions-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('subscriptionsModalTitle')}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="legal-modal-close"
          aria-label={t('footerCloseLabel')}
          onClick={onClose}
        >
          &times;
        </button>
        <h1>{t('subscriptionsModalTitle')}</h1>

        <div className="subscriptions-list">
          {prosvetaComEntry && (
            <SubscriptionsRow
              listKey={prosvetaComEntry[0]}
              info={prosvetaComEntry[1]}
              row={rows[prosvetaComEntry[0]]}
              isProsvetaCom
              onChange={(changes) => updateRow(prosvetaComEntry[0], changes)}
            />
          )}

          {otherEntries.length > 0 && (
            <>
              <h2 className="subscriptions-group-title">{t('subscriptionsGroupTitle')}</h2>
              <div className="subscriptions-group">
                {otherEntries.map(([key, info]) => (
                  <SubscriptionsRow
                    key={key}
                    listKey={key}
                    info={info}
                    row={rows[key]}
                    isProsvetaCom={false}
                    onChange={(changes) => updateRow(key, changes)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="subscriptions-actions">
          <button
            type="button"
            className="subscriptions-btn subscriptions-btn-primary"
            onClick={handleUpdateClick}
          >
            {t('subscriptionsUpdateButton')}
          </button>
          <button
            type="button"
            className="subscriptions-btn subscriptions-btn-danger"
            onClick={handleDeleteAllClick}
          >
            {t('subscriptionsDeleteAllButton')}
          </button>
          <button
            type="button"
            className="subscriptions-btn subscriptions-btn-secondary"
            onClick={onClose}
          >
            {t('subscriptionsCancelButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

function postSubscriptionsUpdate(payload) {
  return fetch(apiUrl('updatesubscriptions'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then((response) => {
    if (!response.ok) {
      throw new Error('update-failed');
    }
    return response.json();
  });
}

export function SubscriptionsManager() {
  const { t } = useTranslation();
  // `null` des deux côtés au premier rendu (le serveur ne connaît pas `window.location`) : lire
  // `?action=subscriptions` dès le rendu client casserait l'hydratation si présent (boîte de
  // dialogue apparaissant côté client mais absente du HTML serveur) — on le lit dans un effet.
  const [actionParams, setActionParams] = React.useState(null);
  const [error, setError] = React.useState('');
  const [lists, setLists] = React.useState(null);
  const [resultMessage, setResultMessage] = React.useState(null);

  React.useEffect(() => {
    setActionParams(getSubscriptionsActionParams());
  }, []);

  React.useEffect(() => {
    if (!actionParams) {
      return;
    }

    if (!actionParams.email || !actionParams.cle) {
      setError(t('subscriptionsMissingParams'));
      return;
    }

    const url = apiUrl('subscriptions');
    url.searchParams.set('email', actionParams.email);
    url.searchParams.set('cle', actionParams.cle);

    fetch(url.toString(), { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(t('subscriptionsLoadError'));
        }
        return response.json();
      })
      .then((data) => {
        if (data && data.error) {
          throw new Error(typeof data.error === 'string' ? data.error : t('subscriptionsLoadError'));
        }
        setLists((data && data.lists) || {});
      })
      .catch((err) => {
        setError((err && err.message) || t('subscriptionsLoadError'));
      });
  }, [actionParams]);

  const handleUpdate = (payload) => {
    postSubscriptionsUpdate(payload)
      .then(() => setResultMessage({ type: 'success', text: t('subscriptionsUpdateSuccess') }))
      .catch(() => setResultMessage({ type: 'error', text: t('subscriptionsUpdateError') }))
      .finally(() => setLists(null));
  };

  const handleDeleteAll = (payload) => {
    postSubscriptionsUpdate(payload)
      .then(() => setResultMessage({ type: 'success', text: t('subscriptionsDeleteAllSuccess') }))
      .catch(() => setResultMessage({ type: 'error', text: t('subscriptionsUpdateError') }))
      .finally(() => setLists(null));
  };

  if (!actionParams) {
    return null;
  }

  if (resultMessage) {
    return (
      <div className={`subscription-status subscription-status--${resultMessage.type}`} role="status">
        {resultMessage.text}
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-status subscription-status--error" role="alert">
        {error}
      </div>
    );
  }

  if (!lists) {
    return null;
  }

  return (
    <SubscriptionsModal
      lists={lists}
      email={actionParams.email}
      onUpdate={handleUpdate}
      onDeleteAll={handleDeleteAll}
      onClose={() => setLists(null)}
    />
  );
}
